import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FullScreenLoader } from "@/components/Spinner";
import { CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import type { PortalState, MVRResult } from "@/store/portal-store";

interface Step8Props {
  state: PortalState;
  updateState: (partial: Partial<PortalState>) => void;
  onNext: () => void;
}

export function Step8MVR({ state, updateState, onNext }: Step8Props) {
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Simulate MVR check
    const timer = setTimeout(() => {
      // Default to "proceed" for wireframe demo. Change to test other states.
      const result: MVRResult = {
        status: "proceed",
      };
      updateState({ mvrResult: result });
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <FullScreenLoader
        message="Checking your driving record..."
        subMessage="This usually takes a moment."
      />
    );
  }

  const result = state.mvrResult;
  if (!result) return null;

  const premium = state.quote?.monthlyPremium ?? 0;

  // PROCEED
  if (result.status === "proceed") {
    return (
      <div className="flex flex-col items-center px-6 py-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Driving Record Verified</h2>
        <p className="text-gray-500 mb-6">
          No changes to your quote. You're all set!
        </p>
        <p className="text-xl font-bold mb-8">Your premium: ${premium}/mo</p>
        <Button
          className="w-full h-12 text-base bg-green-600 hover:bg-green-700"
          onClick={onNext}
        >
          Continue to Payment
        </Button>
      </div>
    );
  }

  // REPRICE
  if (result.status === "reprice") {
    const newPremium = result.newPremium ?? premium + 150;
    return (
      <div className="flex flex-col items-center px-6 py-12 text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="w-12 h-12 text-amber-500" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Premium Updated</h2>
        <p className="text-gray-500 mb-4">
          Based on your driving record, your premium has been adjusted.
        </p>
        <div className="mb-2">
          <span className="text-lg text-gray-400 line-through">
            ${premium}/mo
          </span>
          <span className="text-2xl font-bold text-amber-700 ml-3">
            ${newPremium}/mo
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Due to: {result.reason ?? "Driving record findings"}
        </p>

        <label className="flex items-start gap-3 text-left bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-amber-300 accent-amber-600"
          />
          <span className="text-sm text-amber-800">
            I accept the updated premium of ${newPremium}/mo and authorize Artus
            Insurance to proceed with binding my policy.
          </span>
        </label>

        <Button
          className="w-full h-12 text-base bg-amber-600 hover:bg-amber-700"
          disabled={!accepted}
          onClick={() => {
            if (state.quote) {
              updateState({
                quote: { ...state.quote, monthlyPremium: newPremium },
              });
            }
            onNext();
          }}
        >
          Accept & Continue to Payment
        </Button>
      </div>
    );
  }

  // BLOCK
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <XCircle className="w-12 h-12 text-red-500" />
      </div>
      <h2 className="text-2xl font-bold mb-2">Manual Review Required</h2>
      <p className="text-gray-500 mb-6">
        Your application has been referred to our underwriting team for review.
        We'll contact you within 1 business day with next steps.
      </p>
      <p className="text-sm text-gray-400">
        Questions? Contact us at (888) 555-0199 or support@artusinsurance.com
      </p>
    </div>
  );
}
